/**
 * 史実イベント — 第I章「下剋上」（1467-1520）
 *
 * 章の開始時に必ず発動する「時代法（era-law）」と、
 * 評定マスで引く「事件札（incident）」。
 * docs/01-ゲームデザイン.md §7 / docs/06-賭けと軍配勝負.md
 *
 * 【この章の設計意図】
 * 第I章は「中央の権威が崩れ、実力で国を取る時代が始まる」章である。
 * したがって効果は資源の増減で終わらせず、
 *   ・進軍そのものが鈍る（応仁の乱 → 出目 -1）
 *   ・攻めにくくなる／守りにくくなる（一揆 → 役の段階補正）
 *   ・下剋上札が配られる（明応の政変）
 *   ・兵を使わずに国が取れる（伊勢宗瑞）
 * という形で、史実がルールを書き換えるようにしてある。
 *
 * 執筆基準は provinces/_reference.ts と docs/05-学習設計.md §4 に従う。
 * とくに §4-3（価値判断を持ち込まない）は、宗教勢力と一揆を扱う本章で重い。
 */

import type { HistoricalEvent } from '../types';

// ---------------------------------------------------------------------------
// 1467 応仁の乱 — 第I章の時代法
// ---------------------------------------------------------------------------

/**
 * 時代法。章の開始時に必ず発動する。
 * 効果は3つ。街道が荒れて進軍が鈍り（出目-1）、
 * 幕府に由来する威信が目減りし、各家が自分の判断で動き出す（軍略+1）。
 */
export const ONIN_NO_RAN: HistoricalEvent = {
  id: 'ev:onin-no-ran',
  year: 1467,
  chapter: 1,
  kind: 'era-law',
  name: '応仁の乱',
  reading: 'おうにんのらん',
  effects: [
    // 街道が荒れ、関が乱立し、軍勢も物も動きにくくなる
    { t: 'diceMod', n: -1 },
    // 幕府の下知を待たず、各家が自分の判断で動くようになる
    { t: 'target', who: 'all', then: [{ t: 'gain', res: 'gunryaku', n: 1 }] },
    // 幕府・朝廷の権威に由来していた威信が目減りする
    { t: 'target', who: 'all', then: [{ t: 'lose', res: 'ishin', n: 1 }] },
  ],
  note: {
    summary:
      '将軍家と有力守護家の家督争いが重なり、細川方（東軍）と山名方（西軍）に分かれて11年間戦った。主戦場は京都で、市街の多くが焼けた。',
    detail: {
      1: '1467年、京都で大きな戦いがはじまりました。将軍のあとつぎをめぐる争いなどがかさなり、大名たちが二つに分かれて11年間も戦いました。京のまちの多くがやけ、幕府の力は弱くなりました。',
      2: '1467年（応仁元年）から1477年（文明9年）まで続いた戦乱。将軍足利義政の後継をめぐる問題に、畠山氏・斯波氏の家督争いが重なり、細川勝元を中心とする東軍と山名持豊（宗全）を中心とする西軍に諸国の守護が分かれて戦った。主戦場は京都で、市街の多くが焼けた。戦いには足軽と呼ばれる軽装の兵が加わり、放火や略奪も行われた。乱の後も各地の争いは収まらず、守護が京を離れて領国へ下ったことで、幕府が全国を統率する力は大きく弱まった。',
      3: '応仁元年(1467)から文明9年(1477)まで、京都を主戦場として続いた戦乱。将軍足利義政の後継をめぐる問題に加え、畠山氏・斯波氏という有力守護家の家督争いが絡み、細川勝元を推す東軍と山名持豊（宗全）を推す西軍に諸国の守護が分かれた。戦線は京都市中に固定され、双方が構（かまえ）と呼ばれる陣地を築いて対峙した。足軽と呼ばれる軽装の集団が戦力として現れ、一条兼良は『樵談治要』でその放火・略奪を強く非難している。義政の妻である日野富子は幕府の財政に深く関わり、米の取引や京都七口に設けた関所の関銭で富を蓄えたと伝えられる一方、和睦の仲介にも動いた。乱によって公家や僧が地方へ下ったことは、京都の文化が各地へ広がる契機ともなっている。乱の主因を将軍家の後継争いに求める理解は近年見直され、守護家の家督相続と細川・山名の対立を軸に説明されることが多い。また「応仁の乱で戦国時代が始まった」という時代区分にも異論があり、明応2年(1493)の明応の政変を画期とする見方が有力になっている。',
    },
    year: [1467, 1477],
    keywords: ['応仁の乱', '細川勝元', '山名宗全', '足軽', '日野富子', '京都'],
    caveat:
      '乱の原因を「日野富子と足利義視の後継争い」に求める説明は近年見直され、畠山氏・斯波氏の家督争いと細川・山名の対立を主因とする理解が有力です。戦国時代の始まりを応仁の乱に置くか、明応2年(1493)の明応の政変に置くかも研究者によって分かれます。また「京都が焼け野原になった」という理解も、被害の程度が地区によって差があったことが指摘されています。',
    gameNote:
      '盤上では時代法として「出目-1」「全員の威信-1」「全員の軍略+1」を割り当てていますが、これは街道の荒廃と幕府の権威低下を遊びに落とし込んだ表現です。また盤上では第I章の間 京を領有できない扱いにしていますが、実際には朝廷も室町幕府も京に存在し続けており、京が無主の地になったわけではありません。',
  },
};

// ---------------------------------------------------------------------------
// 1485 山城国一揆
// ---------------------------------------------------------------------------

/**
 * 事件札。国人と百姓が自ら国を守る力を持ったことを、
 * 「攻める側の役が1段階下がる」という形で合戦のルールに落とす。
 */
export const YAMASHIRO_KUNI_IKKI: HistoricalEvent = {
  id: 'ev:yamashiro-kuni-ikki',
  year: 1485,
  chapter: 1,
  kind: 'incident',
  name: '山城国一揆',
  reading: 'やましろのくにいっき',
  effects: [
    // 国人と百姓が寄合で国を運営する。この章の間、攻める側の役が1段階下がる
    { t: 'holeCardMod', n: -1, scope: 'attack' },
    // 引いた者の領国から年貢が届かなくなる
    { t: 'target', who: 'self', then: [{ t: 'lose', res: 'koku', n: 1 }] },
  ],
  note: {
    summary:
      '南山城の国人と百姓が、対陣していた畠山両軍を国外へ退去させ、宇治の平等院で掟を定めて約8年間にわたり自らの手で国を運営した。',
    detail: {
      1: '1485年、いまの京都府の南のほうで、その土地の武士や百姓たちが話し合い、争っていた大名の軍を国から出ていかせました。そして自分たちで決まりをつくり、8年ほどのあいだ、自分たちの手で国をおさめました。',
      2: '文明17年（1485年）12月、山城国南部の久世・綴喜・相楽の三郡で、国人（在地の武士）と百姓が集会を開き、長く対陣を続けていた畠山政長方・畠山義就方の両軍に国外への退去を求め、これを実現させた。翌年2月には宇治の平等院に集まって国掟を定め、三十六人衆と呼ばれる代表による寄合で国の運営にあたった。この自治は約8年続いたが、内部の対立と幕府側の介入により、明応2年（1493年）に終わった。',
      3: '文明17年(1485)12月、山城国南部の上三郡（久世・綴喜・相楽）で、国人と百姓が集会を開き、この地で対陣していた畠山政長方と畠山義就方の双方に国外退去を求め、両軍を退かせた。翌文明18年(1486)2月には宇治の平等院に集まって国掟（国中掟法）を定め、三十六人衆と呼ばれる国人の代表が月行事として交替で国政にあたっている。掟では寺社本所領の年貢納入や新たな関所の停止などが取り決められた。この体制は約8年続き、明応2年(1493)に幕府が伊勢貞陸を山城守護に任じたことと、一揆内部の分裂によって解体した。かつては「農民による自治」「戦国時代の国民議会」と高く評価されることが多かったが、近年は中心となったのが在地領主である国人層であり、彼ら自身が年貢を徴収する立場にあったこと、幕府や守護の権威を全面的に否定したわけではないことが指摘されている。とはいえ、村々の百姓が惣（そう）としてまとまり、軍勢の乱暴狼藉や関所の設置に対して自らの意思を示した点は、この時期の社会の変化を示す出来事である。',
    },
    year: [1485, 1493],
    keywords: ['山城国一揆', '国人', '惣', '平等院', '畠山氏', '自治'],
    caveat:
      '「農民が守護大名を追い出して自治を行った」と説明されることがありますが、近年の研究では中心となったのは在地の武士である国人層で、彼ら自身も年貢を取る立場にありました。「戦国時代の国民議会」といった評価は後世のもので、当時の一揆が近代的な議会や民主主義を目指したわけではありません。',
    gameNote:
      '盤上では「この章の間、攻める側の役が1段階下がる」としていますが、これは国人と百姓が自ら国を守る力を持ったことを合戦のルールに翻訳したものです。実際の山城国一揆が全国の合戦を難しくしたわけではありません。',
  },
};

// ---------------------------------------------------------------------------
// 1488 加賀の一向一揆
// ---------------------------------------------------------------------------

/**
 * 事件札。宗教勢力の扱いは docs/05 §4-3 に従い、事実の記述に留める。
 * 「折り合う」「押さえる」の二択にしてあるが、どちらが正しい選択かは示さない。
 */
export const KAGA_IKKO_IKKI: HistoricalEvent = {
  id: 'ev:kaga-ikko-ikki',
  year: 1488,
  chapter: 1,
  kind: 'incident',
  name: '加賀の一向一揆',
  reading: 'かがのいっこういっき',
  effects: [
    {
      t: 'choice',
      options: [
        {
          // 寺内町の自治を認める。年貢は減るが、市が立ち銭が入る
          label: '門徒と折り合い、寺内町の自治を認める',
          then: [
            { t: 'lose', res: 'koku', n: 1 },
            { t: 'gain', res: 'zeni', n: 2 },
          ],
        },
        {
          // 兵を入れて押さえる。領内に不満が残り、この章の間 守る側の役が1段階下がる
          label: '兵を入れて押さえる',
          then: [
            { t: 'lose', res: 'hei', n: 3 },
            { t: 'holeCardMod', n: -1, scope: 'defend' },
          ],
        },
      ],
    },
  ],
  note: {
    summary:
      '本願寺の門徒と国人が加賀国の守護富樫政親を高尾城に攻め、政親は自害した。以後およそ90年、加賀は守護大名の直接支配を離れた。',
    detail: {
      1: '1488年、加賀（いまの石川県の南のほう）で、本願寺の教えを信じる人びとと土地の武士たちが、守護の富樫政親をせめました。政親は城でなくなり、そのあと90年ほど、加賀は大名がちょくせつおさめない国になりました。',
      2: '長享2年（1488年）、加賀国で本願寺門徒（一向宗と呼ばれた浄土真宗の信者）と国人が結んで守護富樫政親を高尾城に攻め、政親は自害した。門徒側は富樫泰高を名目上の守護に立て、以後は本願寺の坊主衆と国人・門徒の組織が実際の国の運営にあたった。この状態は天正8年（1580年）に織田信長の軍に拠点を落とされるまで、およそ90年続いた。後年、本願寺の実悟は加賀の様子を「百姓ノ持タル国」と書き残している。',
      3: '長享2年(1488)、加賀国の本願寺門徒と国人が守護富樫政親を高尾城に攻め、政親は自害した。背景には、蓮如の布教によって北陸に広がった講という組織と、富樫氏内部の家督争い（政親と富樫幸千代の対立）に門徒が動員された経緯がある。政親を退けたのち、門徒側は富樫泰高を名目上の守護に立て、本願寺の坊主衆・有力国人・門徒の組織が重層的に国を運営した。金沢御坊をはじめとする寺内町では商工業が営まれ、市が立って人と物が集まっている。この体制は天正8年(1580)に織田信長の軍によって拠点が落とされるまで、およそ90年続いた。本願寺の実悟が後年「百姓ノ持タル国ノヤウニナリ行キ候」と記したことから「百姓の持ちたる国」と呼ばれるが、近年の研究では、実際には本願寺と有力国人による支配であって、百姓が国を統治したという理解は成り立ちにくいとされている。攻め手が二十万を超えたとする兵力の数字も、同時代の確かな史料に基づくものではない。',
    },
    year: [1488, 1580],
    keywords: ['加賀', '一向一揆', '本願寺', '蓮如', '富樫政親', '寺内町'],
    caveat:
      '「百姓の持ちたる国」という表現は、後年に本願寺の実悟が書いたものです。近年の研究では、実態は本願寺の坊主衆と有力国人による支配であり、名目上は守護として富樫泰高が置かれていました。攻め手が二十万を超えたという兵力の数字も、同時代の確かな史料に基づくものではありません。',
    gameNote:
      '盤上では「門徒と折り合う／兵を入れて押さえる」の二択にしていますが、これは当時の領主が置かれた立場を選択肢に整理したものです。どちらかが正しい選び方だったという評価を含むものではありません。',
  },
};

// ---------------------------------------------------------------------------
// 1493 明応の政変
// ---------------------------------------------------------------------------

/**
 * 事件札。臣下が将軍を挿げ替えた出来事なので、
 * 「将軍から得ていた威信が目減りし、下剋上札が場に出る」形にする。
 */
export const MEIO_NO_SEIHEN: HistoricalEvent = {
  id: 'ev:meio-no-seihen',
  year: 1493,
  chapter: 1,
  kind: 'incident',
  name: '明応の政変',
  reading: 'めいおうのせいへん',
  effects: [
    // 将軍・幕府に由来していた威信が一斉に目減りする
    { t: 'target', who: 'all', then: [{ t: 'lose', res: 'ishin', n: 2 }] },
    // 主を廃するという前例が生まれる。下位の者ほどこれを頼りにする
    { t: 'target', who: 'last', then: [{ t: 'grantGekokujo', n: 1 }] },
  ],
  note: {
    summary:
      '細川京兆家当主の細川政元が将軍足利義材（のちの義稙）を廃し、足利義澄を新たな将軍に立てた。臣下が将軍を挿げ替えた出来事で、ここを戦国時代の始まりとする見方もある。',
    detail: {
      1: '1493年、幕府で大きな力をもっていた細川政元が、将軍の足利義材（よしき）をやめさせ、べつの人を将軍にしました。家来が主人をとりかえたこの出来事で、将軍の力はいっそう弱くなりました。',
      2: '明応2年（1493年）、幕府の実力者であった細川政元が、河内へ出陣していた将軍足利義材を京都で廃し、堀越公方足利政知の子である義澄を新しい将軍に立てた。義材は捕えられたが脱出し、越中や周防を頼って将軍への復帰を目指した。以後、幕府は二人の将軍候補が並び立つ状態となり、京都の実権は細川氏が握った。将軍が臣下によって挿げ替えられたこの事件を、戦国時代の始まりと位置づける見方がある。',
      3: '明応2年(1493)4月、細川京兆家当主の細川政元は、畠山基家の討伐のため河内に出陣していた将軍足利義材を京都で廃し、伊豆の堀越公方足利政知の子・清晃（還俗して足利義澄）を新将軍に擁立した。義材は捕えられたのち脱出し、越中の神保氏、のち周防の大内氏を頼って将軍への復帰を目指す（のちに義尹・義稙と改名）。以後、幕府は義澄と義稙という二人の将軍候補が並び立つ状態となり、京都の実権は細川氏が握った。政元はこの時点では管領職になく、政変は幕府の職権行使ではなく、京都に残留した政元が日野富子や前政所執事の伊勢貞宗と結んで起こしたクーデターであった。政元が管領に就いたのは、政変の結果として義澄の将軍就任が固まったあとの明応3年(1494)12月である。同じ年に伊勢宗瑞（北条早雲）が伊豆へ討ち入り堀越公方足利茶々丸を追ったのも、この政変と連動した動きとみられている。日野富子の関与の度合いについては史料の解釈に幅がある。政元自身は三人の養子を迎えたことで後継争いを招き、永正4年(1507)に家臣に殺害されて細川家も分裂した（永正の錯乱）。近年の研究では、将軍の権力が決定的な打撃を受けた画期として明応の政変を重視し、応仁の乱ではなくこの年を戦国時代の始まりとする見解が有力になっている。',
    },
    year: 1493,
    keywords: ['明応の政変', '細川政元', '足利義材', '足利義澄', '将軍', '下剋上'],
    caveat:
      '戦国時代の始まりを応仁の乱(1467)に置くか、明応の政変(1493)に置くかは研究者によって分かれます。近年は将軍の権力が決定的に弱まった点を重くみて明応の政変を画期とする見方が有力ですが、区切りが確定しているわけではありません。日野富子の関与についても、史料の解釈に幅があります。',
    gameNote:
      '盤上では「全員の威信-2」「最下位のプレイヤーに下剋上札1枚」としています。下剋上札を下位に配るのは独走を抑えるためのゲーム上の調整（docs/06 §6）を史実の演出に重ねたもので、明応の政変が実際に下位の勢力を助けたという意味ではありません。',
  },
};

// ---------------------------------------------------------------------------
// 1495 伊勢宗瑞、小田原を得る
// ---------------------------------------------------------------------------

/**
 * 事件札。この章の題である「下剋上」を、
 * 兵を使わずに国を切り取れるという形で一度だけ体験させる。
 */
export const SOZUI_ODAWARA: HistoricalEvent = {
  id: 'ev:sozui-odawara',
  year: 1495,
  chapter: 1,
  kind: 'incident',
  name: '伊勢宗瑞、小田原へ',
  reading: 'いせそうずい、おだわらへ',
  effects: [
    // 相模を守る側の役が1段階下がる（doc §7 の「相模の防御-1」に対応）
    { t: 'holeCardMod', n: -1, scope: 'defend' },
    // 引いた者は空白の国を1つ、兵を使わずに切り取れる
    { t: 'target', who: 'self', then: [{ t: 'seize', free: true }] },
  ],
  note: {
    summary:
      '伊勢宗瑞（のちに北条早雲と呼ばれる）が大森氏から小田原城を得た。素浪人が成り上がったという像は現在では否定され、幕府に仕えた伊勢氏一族の東国進出とされる。',
    detail: {
      1: '北条早雲とよばれる伊勢宗瑞が、相模（いまの神奈川県）の小田原城を手に入れました。もとは身分のひくい人が力ではい上がったといわれてきましたが、いまでは幕府につかえた家の出だとわかっています。',
      2: '伊勢宗瑞は、室町幕府の政所執事をつとめた伊勢氏の一族で、幕府の申次衆をつとめた人物。姉妹が駿河の今川義忠に嫁いだ縁で東海へ下り、今川氏の内紛を収めて興国寺城を与えられた。明応2年（1493年）に伊豆へ討ち入って堀越公方足利茶々丸を退け、続いて相模の大森氏から小田原城を得た。永正13年（1516年）には三浦氏を滅ぼして相模を平定している。子の氏綱の代に北条と名乗り、五代にわたって関東を支配する後北条氏の基礎となった。',
      3: '伊勢宗瑞(1456?-1519)は、室町幕府の政所執事をつとめた伊勢氏の一族（備中伊勢氏）に生まれ、幕府の申次衆をつとめた。かつては素性の知れない浪人が一代で大名にのし上がったとされてきたが、近年の研究でこの像は否定されている。姉妹の北川殿が駿河守護今川義忠に嫁いだ縁から東海へ下り、今川氏の家督争いを調停して興国寺城を得た。明応2年(1493)、明応の政変と連動する形で伊豆へ討ち入り、堀越公方足利茶々丸を追っている。小田原城の入手は従来明応4年(1495)とされてきたが、明応5年(1496)の時点で扇谷上杉方として大森藤頼と宗瑞方が小田原を守っていたことを示す史料があり、実際にはそれ以降とする説が有力である。大森氏を欺いて奪ったという理解も見直され、大森藤頼が山内上杉氏へ寝返ったことへの対応とみる説が出ている。永正13年(1516)には三浦氏を新井城に滅ぼして相模を平定した。宗瑞は生前「北条」を名乗っておらず、北条姓は子の氏綱の代に用いられ始めたものである。',
    },
    year: [1493, 1519],
    keywords: ['伊勢宗瑞', '北条早雲', '小田原', '大森氏', '伊豆', '後北条氏'],
    caveat:
      '「北条早雲」は生前の名乗りではなく、本人は伊勢宗瑞と称していました。北条姓は子の氏綱の代からです。小田原城を得た年も明応4年(1495)とされてきましたが、明応5年(1496)以降とする説が有力になっています。大森氏を火牛の計で欺いたという逸話は後世の軍記物によるもので、年貢を四公六民に軽くしたという話も同時代の史料で確かめられているわけではありません。',
    gameNote:
      '盤上では「相模を守る側の役が1段階下がる」「空白の国を1つ、兵を使わずに切り取れる」としていますが、これは下剋上という章のテーマを分かりやすく体験させるための処理です。宗瑞の小田原入手が無血で行われたわけではなく、年も確定していません。',
  },
};

// ---------------------------------------------------------------------------
// 1498 明応地震
// ---------------------------------------------------------------------------

/**
 * 事件札。合戦だけでなく、災害と復旧という生活面を盤上に出すための札。
 * 「人を出して直すか、軍事を優先するか」を選ばせる。
 */
export const MEIO_JISHIN: HistoricalEvent = {
  id: 'ev:meio-jishin',
  year: 1498,
  chapter: 1,
  kind: 'incident',
  name: '明応地震',
  reading: 'めいおうじしん',
  effects: [
    // 沿岸の田畑と湊が失われる
    { t: 'target', who: 'all', then: [{ t: 'lose', res: 'koku', n: 1 }] },
    {
      t: 'choice',
      options: [
        {
          label: '堤を築き、村の復旧に人を出す',
          then: [
            { t: 'lose', res: 'hei', n: 2 },
            { t: 'gain', res: 'koku', n: 1 },
            { t: 'gain', res: 'ishin', n: 2 },
          ],
        },
        {
          label: '兵を割かず、軍事を優先する',
          then: [{ t: 'lose', res: 'ishin', n: 1 }],
        },
      ],
    },
  ],
  note: {
    summary:
      '明応7年8月25日（1498年9月）、東海沖を震源とする大地震と津波が起き、伊勢・志摩から相模にかけて大きな被害が出た。浜名湖が海とつながったのもこのときとされる。',
    detail: {
      1: '1498年、東海の海のそこで大きな地震がおこり、津波がおしよせました。海ぞいの村や港は大きな被害をうけ、浜名湖（いまの静岡県）は海とつながったといわれています。',
      2: '明応7年8月25日（西暦1498年9月）、東海沖を震源とする大地震が起き、伊勢・志摩から駿河・相模にかけての沿岸を津波が襲った。遠江では浜名湖と海を隔てていた砂州が切れ、湖が外海とつながった（今切）。伊勢の大湊では多くの家が流されたと記録されている。この時期は前年から飢饉や疫病も重なっており、災害が続いたことが、1500年の祇園会の再興を後押ししたとみられている。',
      3: '明応7年8月25日（ユリウス暦1498年9月11日、グレゴリオ暦に換算すると9月20日）に発生した、南海トラフ沿いの東海側を震源域とする巨大地震。推定規模はマグニチュード8.2から8.4程度とされる。津波は伊勢・志摩から駿河・相模の沿岸を襲い、伊勢の大湊では家屋が多数流失した。遠江では浜名湖の南を閉ざしていた砂州が破れ、湖が外海と通じるようになった（今切）。この地形の変化は東海道の経路にも影響し、以後この区間は舟で渡ることになった。鎌倉高徳院の大仏殿がこの津波で流されて以後再建されず、露坐の大仏になったという伝承が広く知られるが、近年の調査では大仏殿が失われた時期や原因を津波と断定できないとする指摘がある。当時の記録は公家の日記や寺社の記録に偏っており、沿岸の村々の被害の全体像は分かっていない。地震・津波に加えて飢饉と疫病が重なったことは、明応9年(1500)に京都で祇園会が再興される背景のひとつになったと考えられている。',
    },
    year: 1498,
    keywords: ['明応地震', '津波', '浜名湖', '今切', '南海トラフ', '災害'],
    caveat:
      '鎌倉の大仏殿が明応の津波で流されたという話は広く知られていますが、大仏殿がいつどのように失われたかを津波と断定できる同時代の史料はなく、近年の研究では慎重な見方がされています。地震の日付も暦の換算のしかたによって1498年9月11日と9月20日の両方が使われます。',
    gameNote:
      '盤上では「全員の石高-1」と、復旧に人を割くかどうかの二択を置いています。実際の被害は地域によって大きく異なり、全国が一様に打撃を受けたわけではありません。',
  },
};

// ---------------------------------------------------------------------------
// 1500 祇園会の再興
// ---------------------------------------------------------------------------

/**
 * 事件札。町衆が町を堀と塀で囲んで自衛した事実を、
 * 「京を守る側の役が1段階上がる」という形でルールに置く。
 */
export const GIONE_SAIKO: HistoricalEvent = {
  id: 'ev:gione-saiko',
  year: 1500,
  chapter: 1,
  kind: 'incident',
  name: '祇園会の再興',
  reading: 'ぎおんえのさいこう',
  effects: [
    // 町が構（堀と塀）で囲まれ、町衆が自ら守る。京を守る側の役が1段階上がる
    { t: 'holeCardMod', n: 1, scope: 'defend' },
    // 市に人と物が戻る
    { t: 'target', who: 'all', then: [{ t: 'gain', res: 'zeni', n: 1 }] },
  ],
  note: {
    summary:
      '応仁の乱で33年途絶えていた祇園会（祇園祭）の山鉾巡行が、上京・下京の町衆の手で再興された。巡行の順番を決めるくじ取りもこのとき始まった。',
    detail: {
      1: '1500年、応仁の乱でとだえていた祇園祭の山鉾（やまほこ）が、33年ぶりに京のまちを進みました。まちにすむ商人や職人たち（町衆）が力をあわせて、祭りをよみがえらせたのです。',
      2: '応仁の乱で中断していた祇園会（祇園祭）の山鉾巡行が、明応9年（1500年）に再興された。中断は33年に及んでいた。担い手となったのは上京・下京に住む商工業者である町衆で、彼らは町ごとに山鉾を出し、巡行の順番を決めるくじ取りもこのとき初めて行われた。町衆は町を堀や塀（構）で囲んで自ら守り、町の運営も自分たちで担った。前年までの地震・飢饉・疫病といった災いが続いたことも、疫神を鎮める祭礼の再興を後押ししたとみられている。',
      3: '祇園会は、疫病を鎮めるための御霊会に由来する祇園社（現在の八坂神社）の祭礼で、山鉾の巡行は室町期に町ごとの行事として発達していた。応仁の乱で中断し、明応9年(1500)に33年ぶりに再興される。この再興には幕府奉行人の松田頼亮が関わり、乱以前の山鉾について古老から聞き取って『祇園会山鉾事』に書き留め、巡行の順番を決めるくじ取りを自邸で行った。乱以前には前祭・後祭あわせて六十基ほどの山鉾があったと伝えられ、この記録が当時を知る手がかりになっている。担い手となったのは上京・下京の町衆で、通りの両側を一つの町とする両側町のまとまりを単位に、月行事を交替でつとめて町の運営と自衛にあたった。町は堀と塀（構）で囲まれ、木戸で出入りを管理している。町には商いを営む女性も多く、油・炭・魚などを売り歩く女性の姿が当時の記録や絵画に描かれている。前年までの地震・飢饉・疫病の重なりが、疫神を鎮める祭礼を求める動きにつながったとみられる。',
    },
    year: 1500,
    keywords: ['祇園祭', '町衆', '山鉾', '京都', '両側町', 'くじ取り'],
    caveat:
      '祇園祭の再興を「町衆の自治の力」だけで説明する見方は近年見直され、幕府奉行人の関与や、疫病・災害への不安といった当時の社会状況を含めて理解されるようになっています。',
    gameNote:
      '盤上では「京を守る側の役が1段階上がる」としています。これは町衆が町を堀と塀で囲んで自衛した事実をルールに翻訳したもので、祭礼そのものが防御力を持ったという意味ではありません。',
  },
};

// ---------------------------------------------------------------------------
// 1508 大内義興の上洛
// ---------------------------------------------------------------------------

/**
 * 事件札。「上洛するか、本国を守るか」という戦国大名の条件を、
 * そのまま二択にした札。第III章の信長上洛の60年前にあたる。
 */
export const OUCHI_JORAKU: HistoricalEvent = {
  id: 'ev:ouchi-joraku',
  year: 1508,
  chapter: 1,
  kind: 'incident',
  name: '大内義興の上洛',
  reading: 'おおうちよしおきのじょうらく',
  effects: [
    {
      t: 'choice',
      options: [
        {
          // 京へ上る。威信は得るが、留守の本国は手薄になる
          label: '将軍を奉じて上洛する',
          then: [
            { t: 'moveTo', square: 0 },
            { t: 'gain', res: 'ishin', n: 3 },
            { t: 'lose', res: 'koku', n: 2 },
          ],
        },
        {
          // 中央には関わらず、領国を固める
          label: '本国の経営を選ぶ',
          then: [
            { t: 'gain', res: 'koku', n: 2 },
            { t: 'gain', res: 'gunryaku', n: 1 },
          ],
        },
      ],
    },
  ],
  note: {
    summary:
      '周防の大内義興が前将軍足利義稙を奉じて上洛し、義稙を将軍に復帰させた。義興は約10年間京都に留まったが、その間に領国が揺らぎ、1518年に帰国した。',
    detail: {
      1: '1508年、周防（いまの山口県）の大内義興が、将軍をやめさせられていた足利義稙をつれて京都へのぼり、もういちど将軍にもどしました。しかし義興が10年も京にいるあいだに国もとがあぶなくなり、帰ることになりました。',
      2: '永正5年（1508年）、周防・長門などを領した大内義興は、明応の政変で将軍の座を追われていた足利義稙を奉じて上洛し、義稙を将軍に復帰させた。義興は管領代となり、細川高国とともに幕府の政治を担った。しかし在京は約10年に及び、その間に出雲の尼子経久が勢力を伸ばし、安芸の国人にも離反の動きが出たため、永正15年（1518年）に領国へ帰った。中央の政治に関わることと領国を保つことを両立させる難しさを示す例とされる。',
      3: '大内義興(1477-1529)は周防・長門・豊前・筑前などを領した守護大名。永正5年(1508)、細川政元の死後に京都で起きた混乱に乗じ、明応の政変で追われて周防に亡命していた前将軍足利義稙を奉じて上洛し、義稙を将軍に復帰させた。義興自身は管領代となり、細川高国と並んで幕府の政務にあたっている。大内氏の力の基盤には、博多を押さえて日明貿易（勘合貿易）に大きく関与していたことがあり、本拠の山口には京都から公家や僧が下って文化が集まった。しかし義興の在京は10年に及び、その間に出雲の尼子経久が石見・安芸へ勢力を広げ、安芸の国人にも動揺が生じたため、永正15年(1518)に帰国する。以後、義興が再び上洛することはなかった。中央の権威に近づくことと領国を保つことがしばしば両立しないという、戦国期の大名が置かれた条件をよく示す事例であり、織田信長の上洛(1568)より60年前の出来事にあたる。',
    },
    year: [1508, 1518],
    keywords: ['大内義興', '上洛', '足利義稙', '勘合貿易', '尼子経久', '山口'],
    caveat:
      '大内氏が日明貿易を独占していたという説明は単純化で、細川氏との競合が続き、大永3年(1523)には明の寧波で両者の使節が衝突する事件（寧波の乱）も起きています。',
    gameNote:
      '盤上では「将軍を奉じて上洛する（京へ移動・威信+3・石高-2）」か「本国の経営を選ぶ（石高+2・軍略+1）」の二択にしています。義興が実際にこの二つを天秤にかけて選んだという記録があるわけではなく、10年の在京とその後の帰国という経過を、一手の選択に圧縮した表現です。',
  },
};

// ---------------------------------------------------------------------------

/** 第I章のイベント一覧。年代順。 */
export const CHAPTER1_EVENTS: readonly HistoricalEvent[] = [
  ONIN_NO_RAN,
  YAMASHIRO_KUNI_IKKI,
  KAGA_IKKO_IKKI,
  MEIO_NO_SEIHEN,
  SOZUI_ODAWARA,
  MEIO_JISHIN,
  GIONE_SAIKO,
  OUCHI_JORAKU,
];
